import styled, { css } from 'styled-components';
import { Link as RawLink } from 'react-router-dom';
import { color, media, size, spacing } from './';

export const DetailListItemLink = styled(RawLink)`
   position: relative;
   display: block;
   padding: ${spacing()};
   margin-bottom: ${spacing(0.5)};
   text-decoration: none;
   color: inherit;
   border-radius: ${({ theme }) => theme.borderRadius};
   background-color: #fff;
   border: 1px solid ${({ theme }) => theme.separator};
   transition: box-shadow 150ms ease, background-color 150ms ease;

   ${props =>
      (props.isBare || props.isLast) &&
      css`
         margin-bottom: 0;
      `};

   &:hover {
      svg {
         opacity: 1;
      }
   }

   ${props =>
      (Boolean(props.to) || Boolean(props.href)) &&
      css`
         border: 1px solid ${({ theme }) => theme.separator};
         box-shadow: 0 2px 2px rgba(0, 0, 0, 0.06);

         &:hover {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
         }
      `};

   ${props =>
      props.hasNoBorder &&
      css`
         border: 0;
      `};

   ${props =>
      props.variant === 'withShadow' &&
      css`
         border: 0;
         box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);

         &:hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
         }
      `};

   ${props =>
      props.variant === 'primaryWithShadow' &&
      css`
         background-color: #c9e9eb;
         border: 0;
         box-shadow: inset 0 0 0 1px rgba(10, 139, 220, 0.16), 0 4px 8px rgba(0, 0, 0, 0.06);

         &:hover {
            box-shadow: inset 0 0 0 1px rgba(10, 139, 220, 0.16), 0 4px 8px rgba(0, 0, 0, 0.12);
         }
      `};

   ${props =>
      props.variant === 'raw' &&
      css`
         padding: 0 ${spacing()};
         background-color: transparent;
         border: 0;
         margin-bottom: 0;

         &,
         &:hover {
            box-shadow: none;
         }
      `};

   ${props =>
      props.isPrimary &&
      css`
         background-color: ${({ theme }) => theme.primary};
         color: ${({ theme }) => theme.white};
      `};
`;

export const DetailListItemExternalLink = DetailListItemLink.withComponent('a');

export const DetailListItem = styled.li`
   ${props =>
      props.padded &&
      css`
         padding: ${spacing(0.75)} 0;
      `};

   ${props =>
      props.isMargedBottom &&
      css`
         margin-bottom: ${spacing()};
      `};

   + li {
      position: relative;
   }

   ${props =>
      props.tight &&
      css`
         > * {
            padding: ${spacing(0.5)} !important;
         }
      `};

   ${({ isHighlighted }) =>
      isHighlighted &&
      css`
         background-color: ${({ theme }) => theme.backgroundLight};
      `}
`;

export const DetailList = styled.ul`
   padding-left: 0;
   margin-top: 0;
   margin-bottom: ${spacing(2)};
   list-style-type: none;

   ${props =>
      props.stacked &&
      css`
         border: 1px solid ${({ theme }) => theme.separator};
         border-radius: ${({ theme }) => theme.borderRadius};
         background-color: #fff;

         ${DetailListItem} > * {
            border: 0;
            border-radius: 0;
            margin-bottom: 0;
            background-color: transparent;
         }

         ${DetailListItem} + li > * {
            position: relative;

            &:before {
               content: '';
               display: block;
               position: absolute;
               left: ${({ theme }) => theme.spacing()};
               top: 0;
               right: 0;
               border-top: 1px solid ${({ theme }) => theme.separator};
            }
         }

         ${DetailListItemLink} {
            box-shadow: none;

            &:hover {
               box-shadow: none;
               background-color: ${({ theme }) => theme.backgroundLight};
            }
         }
      `};

   ${props =>
      props.carded &&
      css`
         ${DetailListItem} > * {
            border-radius: ${({ theme }) => theme.borderRadius};
            margin-bottom: ${({ theme }) => theme.borderRadius};
            border: 1px solid ${({ theme }) => theme.separator};
         }

         ${DetailListItem} + li {
            margin-top: ${spacing()};
         }
      `};

   ${props =>
      props.bare &&
      css`
         border: 0;
         border-radius: 0;
         overflow: initial;
         background-color: transparent;
      `};

   ${props =>
      props.isStatic &&
      css`
         border: 0;
      `};

   ${props =>
      props.isMarged &&
      css`
         margin-top: ${spacing(2)};
         margin-bottom: ${spacing(2)};
      `};

   ${props =>
      props.isBareBottom &&
      css`
         margin-bottom: 0;
      `};

   ${props =>
      props.withTopItemMargin &&
      css`
         ${DetailListItem} {
            margin-top: ${spacing(1.5)};
         }
      `};
`;

export const DetailListItemContent = styled(DetailListItemLink)`
   background-color: transparent;
   padding-left: 0;
   padding-right: 0;
   padding-top: ${spacing(0.75)};
   padding-bottom: ${spacing(0.75)};
   border: 0;
   display: flex;

   ${props =>
      props.isPadded &&
      css`
         padding-left: ${spacing()};
         padding-right: ${spacing()};
         padding-top: ${spacing()};
         padding-bottom: ${spacing()};
      `};

   ${props =>
      props.isPaddedHorizontally &&
      css`
         padding-left: ${spacing()};
         padding-right: ${spacing()};
         padding-top: 0;
         padding-bottom: 0;

         + p {
            margin-top: ${spacing(0.75)};
         }
      `};

   ${props =>
      props.isBare &&
      css`
         padding-top: 0;
         padding-bottom: 0;
         border: 0 !important;
      `};

   &:hover {
      background-color: transparent;
   }
`.withComponent('div');

export const DetailListItemButton = styled(DetailListItemLink)`
   display: block;
   width: 100%;
   text-align: inherit;
   cursor: pointer;
   outline: none;
`.withComponent('button');

export const DetailListItemTitle = styled.h2`
   font-weight: 600;
   margin: 0;
   font-size: ${size(1)};
   flex: 0 0 100%;
   color: inherit;
   text-decoration: none;

   ${props =>
      props.small &&
      css`
         font-size: ${size()};
      `};

   ${props =>
      props.isSubtle &&
      css`
         font-weight: 400;
      `};

   > span,
   > img {
      display: inline-block;
      vertical-align: middle;
   }

   ${props =>
      props.isBig &&
      css`
         ${media.tablet`
        font-size: ${size(2)};
      `};
      `};
`;

export const DetailListItemLabel = styled.div`
   margin-top: ${spacing(0.25)};
   color: ${({ theme }) => theme.textLight};

   ${props =>
      props.small &&
      css`
         font-size: ${size(-1)};
      `};

   ${props =>
      props.exceeded &&
      css`
         color: ${({ theme }) => theme.warning};
      `};

   ${props =>
      props.dark &&
      css`
         color: ${({ theme }) => theme.text};
      `};

   ${({ variant }) =>
      variant === 'onTop' &&
      css`
         margin-top: 0;
         margin-bottom: ${({ theme }) => theme.spacing(0.25)};
      `}
`;

export const DetailListItemRight = styled.div`
   display: table-cell;
   vertical-align: middle;
`;

export const LinkInner = styled.div`
   display: flex;
   width: 100%;
   align-items: center;
   color: inherit;
   text-decoration: none;

   ${props =>
      props.alignTop &&
      css`
         align-items: flex-start;
      `};
`;

export const Meta = styled.div`
   display: flex;
   flex: 1;
   vertical-align: top;
   flex-wrap: wrap;
`;

export const Icon = styled.div`
   display: flex;
   vertical-align: middle;
   font-size: ${size(2)};
   padding-left: ${spacing()};
   line-height: 0.8;
   white-space: nowrap;
   align-items: center;

   > svg {
      fill: ${({ theme }) => theme.primary};
   }

   ${props =>
      props.arrow &&
      css`
         > svg {
            fill: ${color('grey', 'dark')};
         }
      `};
`;

export const DetailListItemDate = styled.time`
   float: right;
   line-height: ${size(2)};
   color: ${({ theme }) => theme.textLight};

   ${props =>
      props.variant === 'small' &&
      css`
         font-size: ${size(-1)};
      `};
`;

export const DetailLIstItemTitleBody = styled.span`
   line-height: 24px;
   vertical-align: middle;
`;

export const DetailListItemHelp = styled.p`
   font-size: 14px;
   color: ${({ theme }) => theme.primary};
   line-height: 1.5;
   border: 1px solid ${({ theme }) => theme.primary};
   border-radius: 4px;
   padding: ${spacing()};
   margin: 0 0 ${spacing(2)};

   a + & {
      margin-top: ${spacing(0.75)};
   }
`;
